import { PwaConfig } from "../Pwa.js";

type UnsubscribeState = "unsubscribed" | "unsupported" | "not-subscribed" | "failed";
type SubscribeState = "subscribed" | "unsupported" | "already-subscribed" | "denied";


export interface SubscribeOptions {
    locale?: string;
}

export type SubscriptionPermission = NotificationPermission | "unsupported" | "prompt";

export class NotificationHandler {

    public readonly onChange = e5.Signal.create<[boolean]>();
    public readonly onChangePermission = e5.Signal.create<[SubscriptionPermission]>();
    public readonly enabled: boolean = false;

    constructor(private _config: PwaConfig) {

        this.enabled = _config.notificationsEnabled;

        if (this.supported) {
            this.init();
        }
    }

    private async init() {

        /**
         * For debugging
         */
        var notify = e5.Debug.add({
            uid: "notify",
            tooltip: "Notify",
            icon: "fal fa-bell"
        });
        notify.element.addEventListener("click", async () => {
            if (Notification.permission !== "granted") {
                e5.console.log("Request permission for notifications");
                await Notification.requestPermission();
            }

            var perm = await navigator.permissions
                .query({ name: 'notifications' })
                .catch((e) => {
                    console.warn(e);
                });
            if (perm) {
                e5.console.log("Permission state:", perm.state);
            }

            var r = await window.navigator.serviceWorker.getRegistration();
            var k = await r.showNotification("Test", {
                body: "Message"
            });

            e5.console.log("Show notification finished!");
        });

        //Note: As of Firefox 44, the permissions for Notifications and Push have been merged. 
        //If permission is granted for notifications, push will also be enabled.
        navigator.permissions.query({ name: "notifications" }).then((status) => {
            status.addEventListener("change", async (e) => {
                var isSub = await this.isSubscribed();
                try {
                    if (!isSub && localStorage.getItem("e5_subscription_endpoint")) {
                        this.removeEndpoint(localStorage.getItem("e5_subscription_endpoint"));
                    }
                } catch (error) {
                    console.warn("Error removing old endpoint.");
                }
                this.onChangePermission.dispatch(status.state);
                this.onChange.dispatch(isSub);
            });
        });

        // navigator.permissions.query({ name: "push", userVisibleOnly: true } as any).then((status) => {
        //     status.addEventListener("change", (e) => {
        //         console.log("PUSH", status.state);

        //         //this.onChange.dispatch(false);
        //     });
        // });

        // window.addEventListener("push")
    }

    public get permission(): SubscriptionPermission {
        if (!this.supported) {
            return "unsupported";
        }
        return Notification.permission;
    }

    public async subscribe(options: SubscribeOptions = {}): Promise<SubscribeState> {
        if (!this.supported) {
            return "unsupported";
        }

        options = e5.object.merge({

        } as SubscribeOptions, options);

        if (!options.locale) {
            options.locale = this.getLocale();
        }

        let reg = await navigator.serviceWorker.ready;
        var sub = await reg.pushManager.getSubscription();

        if (sub) {
            return "already-subscribed";
        }

        try {
            sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this._config.publicKey
            });
        } catch (error) {
            console.warn(error);
            return "denied";
        }

        var response = await e5.load("json",
            this._config.subscribeUrl,
            {
                method: "POST",
                data: JSON.stringify(
                    {
                        options: options,
                        subscription: sub.toJSON()
                    })
            });

        try {
            localStorage.setItem("e5_subscription_endpoint", sub.endpoint);
        } catch (error) {
            console.warn("Could not save subscription endpoint to local storage.");
        }

        e5.console.log("RESPONSE", response);

        return "subscribed";
    }

    public async unsubscribe(): Promise<UnsubscribeState> {
        if (!this.supported) {
            return "unsupported";
        }

        var reg = await navigator.serviceWorker.ready;
        var sub = await reg.pushManager.getSubscription();

        if (!sub) {
            return "not-subscribed";
        }

        var endpoint = sub.endpoint;

        var res = await sub.unsubscribe();

        await this.removeEndpoint(endpoint);

        return res ? "unsubscribed" : "failed";
    }

    private async removeEndpoint(endpoint: string): Promise<boolean> {
        var response = await e5.load("json",
            this._config.unsubscribeUrl,
            {
                method: "POST",
                data: JSON.stringify(
                    {
                        endpoint: endpoint
                    })
            });

        try {
            localStorage.removeItem("e5_subscription_endpoint");
        } catch (error) {
            console.warn("Could not save subscription endpoint to local storage.");
        }

        //TODO: handle response
        e5.console.log("Remove Endpoint", response);

        return true;
    }

    public async isSubscribed(): Promise<boolean> {
        if (!this.supported) {
            return false;
        }

        // if (Notification.permission !== "granted") {
        //     return false;
        // }

        try {
            var reg = await navigator.serviceWorker.ready;
            if (!reg) {
                return false;
            }

            var sub = await reg.pushManager.getSubscription();
        } catch (error) {
            return false;
        }

        return sub ? true : false;
    }

    public get supported(): boolean {
        return ('Notification' in window)
            && ("permissions" in navigator)
            && ('serviceWorker' in navigator)
            && ('getRegistration' in navigator.serviceWorker);
    }

    private getLocale(): string {
        //Get the language from Facebook bot

        var url = new URL(window.location.href);

        var lang = (navigator.language || (<any>navigator).userLanguage);

        if (lang) {
            return lang;
        }
        else if (url.searchParams.has("fb_locale")) {
            return url.searchParams.get("fb_locale") == "de_DE" ? "de" : "en";
        }
        //Get the language from the "lang" GET-parameter
        else if (url.searchParams.has("lang")) {
            return url.searchParams.get("lang");
        }
        //Language by <html lang="">
        else if (document.documentElement.hasAttribute("lang")) {
            return document.documentElement.getAttribute("lang");
        }

        return null;
    }
}